import React, { useMemo, useEffect, useState, useCallback } from 'react'
import { MaterialReactTable } from 'material-react-table'
import { toast } from 'react-toastify'
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
// eslint-disable-next-line camelcase
import { MRT_Localization_ES } from 'material-react-table/locales/es'
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material'
import {
  getAdminModelosAplicacion,
  crearAdminModeloAplicacion,
  actualizarAdminModeloAplicacion,
  eliminarAdminModeloAplicacion,
} from '@auteco/services/motosSnopServices'

const AdminModelosContainer = () => {
  const [data, setData] = useState([])
  const [modelosParaSelect, setModelosParaSelect] = useState([])
  const [openDialog, setOpenDialog] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState({
    id_modelo_planeacion: null,
    modelo_planeacion: '',
    modelo_reemplazante: '',
  })

  const cargarDatos = useCallback(async () => {
    const response = await getAdminModelosAplicacion()
    if (response.success) {
      const modelos = response.data || []
      setData(modelos)
      setModelosParaSelect(modelos)
    } else {
      toast.error(response.message || 'Error al cargar los datos')
    }
  }, [])

  // Cargar datos iniciales
  useEffect(() => {
    cargarDatos()
  }, [cargarDatos])

  const handleOpenDialog = (row = null) => {
    if (row) {
      setIsEditing(true)
      setFormData({
        id_modelo_planeacion: row.original.id_modelo_planeacion,
        modelo_planeacion: row.original.modelo_planeacion,
        modelo_reemplazante: row.original.modelo_reemplazante || '',
      })
    } else {
      setIsEditing(false)
      setFormData({
        id_modelo_planeacion: null,
        modelo_planeacion: '',
        modelo_reemplazante: '',
      })
    }
    setOpenDialog(true)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setFormData({
      id_modelo_planeacion: null,
      modelo_planeacion: '',
      modelo_reemplazante: '',
    })
  }

  const handleSubmit = async () => {
    if (!formData.modelo_planeacion.trim()) {
      toast.error('El modelo de planeación es requerido')
      return
    }

    const dataToSend = {
      modelo_planeacion: formData.modelo_planeacion,
      modelo_reemplazante: formData.modelo_reemplazante || null,
    }

    if (isEditing) {
      dataToSend.id_modelo_planeacion = formData.id_modelo_planeacion
      const response = await actualizarAdminModeloAplicacion(dataToSend)
      if (response.success) {
        toast.success(response.message)
        cargarDatos()
        handleCloseDialog()
      } else {
        toast.error(response.message)
      }
    } else {
      const response = await crearAdminModeloAplicacion(dataToSend)
      if (response.success) {
        toast.success(response.message)
        cargarDatos()
        handleCloseDialog()
      } else {
        toast.error(response.message)
      }
    }
  }

  const handleDelete = async (row) => {
    // eslint-disable-next-line no-restricted-globals, no-alert
    const confirmado = confirm(
      `¿Está seguro que desea eliminar el modelo "${row.original.modelo_planeacion}"?`,
    )
    if (confirmado) {
      const response = await eliminarAdminModeloAplicacion(
        row.original.id_modelo_planeacion,
      )
      if (response.success) {
        toast.success(response.message)
        cargarDatos()
      } else {
        toast.error(response.message)
      }
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: 'id_modelo_planeacion',
        header: 'ID',
        size: 80,
      },
      {
        accessorKey: 'modelo_planeacion',
        header: 'Modelo Planeación',
        size: 200,
      },
      {
        accessorKey: 'modelo_reemplazante',
        header: 'Modelo Reemplazante',
        size: 200,
        Cell: ({ cell }) => cell.getValue() || 'N/A',
      }
    ],
    [],
  )

  return (
    <Box sx={{ padding: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 2,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleOpenDialog()}
        >
          Crear Nuevo Modelo
        </Button>
      </Box>

      <MaterialReactTable
        columns={columns}
        data={data}
        localization={MRT_Localization_ES} // eslint-disable-line camelcase
        enableRowActions
        positionActionsColumn="last"
        renderRowActions={({ row }) => (
          <Box sx={{ display: 'flex', gap: '0.5rem' }}>
            <Tooltip title="Editar">
              <IconButton
                color="primary"
                onClick={() => handleOpenDialog(row)}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Eliminar">
              <IconButton color="error" onClick={() => handleDelete(row)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        muiTablePaperProps={{
          elevation: 2,
          sx: {
            borderRadius: '8px',
          },
        }}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
      />

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isEditing ? 'Editar Modelo' : 'Crear Nuevo Modelo'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <TextField
              label="Modelo Planeación"
              fullWidth
              required
              value={formData.modelo_planeacion}
              onChange={(e) =>
                setFormData({ ...formData, modelo_planeacion: e.target.value })
              }
              disabled={isEditing}
              helperText={
                isEditing
                  ? 'El modelo de planeación no puede ser modificado'
                  : 'Ingrese el nombre del modelo de planeación'
              }
            />
            <FormControl fullWidth>
              <InputLabel>Modelo Reemplazante</InputLabel>
              <Select
                value={formData.modelo_reemplazante}
                label="Modelo Reemplazante"
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    modelo_reemplazante: e.target.value,
                  })
                }
              >
                <MenuItem value="">
                  <em>Ninguno</em>
                </MenuItem>
                {modelosParaSelect
                  .filter(
                    (modelo) =>
                      modelo.modelo_planeacion !== formData.modelo_planeacion,
                  )
                  .map((modelo) => (
                    <MenuItem
                      key={modelo.id_modelo_planeacion}
                      value={modelo.modelo_planeacion}
                    >
                      {modelo.modelo_planeacion}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancelar
          </Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {isEditing ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default AdminModelosContainer
